import { useState, useEffect } from "react";
import { Divider, Grid, Typography } from "@mui/material";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  MetaTags,
  PageLayout,
  CreateActions,
  Step,
  Stepper,
} from "../../components";

import useUser from "../../hooks/useUser";
import { useCreateMutation, useGetListQuery } from "../../features/api/Api";
import { useLocales } from "../../hooks";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectCustomerAddressLocation,
  selectIdentityCardTypes,
  setDisabledFields,
  setIdentityCardTypes,
} from "../../features/customer/customerSlice";
import { ConditionProps, customerInitials } from "../../interfaces/Customer";
import {
  clean,
  customerCommunicationTypeEnum,
  customerKindEnum,
  isExists,
  params,
  setAddress,
  setContact,
  setCustomerTags,
  setFormattedDate,
  setFormattedDateWithLocale,
  workOrderStatusEnum,
  workOrderTypeEnum,
} from "../../utils";
import { saveHandler } from "../../handlers";
import { addressProduct } from "../../interfaces/Address";
import {
  addressValidationSchema,
  documentValidationSchema,
  generalValidationSchema,
  serviceValidationSchema,
} from "./common";
import {
  CustomerAddressCreate,
  DocumentCreate,
  GeneralCreate,
  RelatedPersonCreate,
  ServiceCreate,
} from ".";
import { setOrderLines } from "./service/common";
import { nanoid } from "nanoid";

const ApplicationCreate = () => {
  const { user }: any = useUser();
  const { translate } = useLocales();
  const [create] = useCreateMutation();
  const history = useNavigate();
  const addressLocations = useAppSelector(selectCustomerAddressLocation);
  const dispatch = useAppDispatch();
  const identityCardTypes = useAppSelector(selectIdentityCardTypes);
  const [condition, setCondition] = useState<ConditionProps>({
    customerTypeId: 0,
    isForeign: false,
    eConversion: false,
  });
  const [existCustomer, setExistCustomer] = useState(false);

  const { data: identityCardTypesData } = useGetListQuery<any>({
    resource: "fixity/customer/identityCardType",
    params,
  });

  useEffect(() => {
    if (isExists(identityCardTypesData?.data)) {
      dispatch(setIdentityCardTypes(identityCardTypesData.data));
    }
  }, [identityCardTypesData, dispatch]);

  function createMethodWorkOrder(values: any, { setSubmitting }: any) {
    setSubmitting(true);
    create({
      resource: "fixity/workorder/workorder",
      params: values,
    })
      .unwrap()
      .then((response: any) => {
        Swal.fire(translate("actions.saved"), "", "success");
        history(`/applications`);
      })
      .catch((err: any) => {
        const error = err.data?.errors?.[0];
        Swal.fire(translate("actions.error"), error, "error");
        setSubmitting(false);
      });
  }

  function createMethod(values: any, { setSubmitting }: any) {
    setSubmitting(true);
    create({
      // resource: "fixity/customer/customer",
      resource: "fixity/customer/customer/create-customer-merlin",
      params: values.customer,
    })
      .unwrap()
      .then((res: any) => {
        //Swal.fire(translate("actions.saved"), "", "success");
        const customerId = res.data.customerId;
        //setSubmitting(true);
        //history(`/customers/${customerId}/general`);
        values.workOrder.customerId = customerId;
        saveHandler(
          clean(values.workOrder),
          { setSubmitting },
          createMethodWorkOrder
        );
        dispatch(
          setDisabledFields({
            name: false,
            surname: false,
            commercialTitle: false,
            alias: false,
            isEInvoiceUser: true,
          })
        );
        setSubmitting(false);
      })
      .catch((err: any) => {
        const error = err.data?.errors?.[0];
        Swal.fire(translate("actions.error"), error, "error");
        setSubmitting(false);
      });
  }

  function submitHandler(values: any, { setSubmitting }: any) {
    // setSubmitting(true);
    const customerValues: any = {
      name: values.name,
      surname: values.surname,
      alias: values.alias,
      email: values.email,
      sectorId: process.env.REACT_APP_SECTORID,
      ownerId: process.env.REACT_APP_OWNERID,
      customerTypeId: values.customerTypeId,
      customerKindId: Number(values.customerKindId),
      adSourceTypeId: values.adSourceTypeId,
      citizenOrTaxNumber: values.citizenOrTaxNumber,
      commercialTitle: values.commercialTitle,
      contact: [setContact(values.contact, values.isExistCustomer)],
      customerTags: setCustomerTags(values.customerTags),
      // address:
      //   values.customerTypeId === customerTypeEnum.real ||
      //   values.customerDetail.eConversion
      //     ? [setAddress(values.address, values.creatorEmail)]
      //     : null,
      address: [setAddress(values.address, values.creatorEmail)],
      creatorId: user.customerId,
      creatorEmail: user?.email,
      customerAlarmSeverityTypes: values.customerAlarmSeverityTypes,
      customerAlarmTypes: values.customerAlarmTypes,
      customerDetail: {
        ...values.customerDetail,
        businessPartnerId: user.customerId,
      },

      isAuthorizedPerson: values.isAuthorizedPerson,
      isAuthorizedSignature: values.isAuthorizedSignature,
      customerCommunications: [
        {
          communicationTypeId: customerCommunicationTypeEnum.publicEmail,
          value: values.contact.contactCommunication[0].value,
          description: "Genel E-Posta",
          id: nanoid(),
          isDefaultCommunication: true,
        },
        {
          communicationTypeId: customerCommunicationTypeEnum.technicalEmail,
          value: values.contact.contactCommunication[0].value,
          description: "Teknik E-Posta",
          id: nanoid(),
          isDefaultCommunication: true,
        },
      ],
    };

    if (values.isExistCustomer) {
      customerValues.dateOfBirth = values.dateOfBirth;
    } else {
      customerValues.dateOfBirth =
        Number(values.customerKindId) === customerKindEnum.indivual
          ? setFormattedDateWithLocale(values.dateOfBirth, "tr")
          : null;
    }
    const workOrderValues = {
      statusId: workOrderStatusEnum.open,
      typeId: workOrderTypeEnum.request,
      customerId: "",
      priorityId: 1,
      organizationGroupId: "95fdc3f9-2240-46bf-800d-02d13df96bd8",
      title:
        values.customerKindId === customerKindEnum.indivual
          ? `${values.name} ${values.surname} - ${setFormattedDate(
              new Date()
            )} + Hizmet Başvuru Talebi`
          : `${values.commercialTitle} - ${setFormattedDate(
              new Date()
            )} + Hizmet Başvuru Talebi`,
      description: `Müşteri İsmi : ${
        values.customerKindId === customerKindEnum.indivual
          ? values.name + " " + values.surname
          : values.commercialTitle
      }
      Müşteri TCKN / VKN No : ${values.citizenOrTaxNumber}
      ${
        values.customerKindId === customerKindEnum.indivual
          ? "Müşteri Doğum Tarihi :" +
            setFormattedDateWithLocale(values.dateOfBirth, "tr")
          : ""
      }

      ====================================================
      İlgili Kişi TCKN : ${values.contact.identityNo}
      İlgili Kişi İsim : ${values.contact.name}
      İlgili Kişi Soyadı : ${values.contact.surname}
      İlgili Kişi Doğum Tarihi :  ${
        values.contact.dateOfBirth &&
        setFormattedDateWithLocale(values.contact.dateOfBirth, "tr")
      }
      İlgili Kişi E-Posta : ${values.contact.contactCommunication[0].value}
      İlgili Kişi Telefon Numarası : ${
        values.contact.contactCommunication[1].value
      }
      

      ${setOrderLines(values.orderLines)} ${values.workorderDescription}`,
      detail: {
        actionFieldId: 1,
        // categoryId: "10aa667c-4874-402f-f916-08da3d92af62", //canlıya böle çıkacak
        categoryId: "00000006-0000-0000-0000-000000000000",
      },
      documents: values.documents,
    };
    if (!values.isExistCustomer) {
      const val = {
        customer: customerValues,
        workOrder: workOrderValues,
      };

      saveHandler(clean(val), { setSubmitting }, createMethod);
    } else {
      workOrderValues.customerId = values.customerId;
      saveHandler(
        clean(workOrderValues),
        { setSubmitting },
        createMethodWorkOrder
      );
    }
  }

  return (
    <>
      <MetaTags
        title={translate("new_application")}
        keyword="Fixity, Category, Edit"
        description="Category Edit Page"
      />
      <PageLayout
        title={translate("new_application")}
        actions={<CreateActions path="applications" />}
      >
        <Stepper
          onSubmit={submitHandler}
          initialValues={{
            isExistCustomer: false,
            customerId: "",
            ...customerInitials,
            creatorId: user.customerId,
            categoryId: "",
            paymentType: null,
            paymentSesType: null,
            newType: null,
            hatType: "",
            serviceType: "",
            pstnPhoneNumbers: [
              {
                phone: "",
                range: "",
              },
            ],
            pri: [
              {
                phone: "",
                block: "",
                //didcount: "",
              },
            ],
            bri: [
              {
                phone: "",
                block: "",
                didcount: "",
              },
            ],
            addressProduct: addressProduct,
            internetAddresses: [],
            devreNo: "",
            servicePhone: "",
            selectAddressCategory: 0,
            categoryName: "",
            productName: "",
            campaignName: "",
            staticIp: false,
            phoneInternet: "",
            customerManagementGroupIds: [],
            customerManagementGroupNames: [],
            ddtechType: null,
            ddtectTypeName: "",
            orderLines: [],
            internetTypeId: 0,
            invoiceGroupName: "",
            internetInfrustructure: [],
            fullAddress: "",
            addressInternetSpeed: "",
            documents: [
              {
                documentCategoryId: 1,
                documentFileId: "",
                description: "",
                file: {
                  base64: "",
                  contentType: "",
                  fileName: "",
                },
              },
            ],
          }}
        >
          <Step
            label={translate("tabs.header.general")}
            validationSchema={generalValidationSchema(identityCardTypes)}
          >
            <GeneralCreate
              setCondition={setCondition}
              setExistCustomer={setExistCustomer}
            />
            <Grid item md={12}>
              <Divider style={{ marginTop: "30px", marginBottom: "15px" }} />
              <Typography variant="h6">Yetkili Kişi</Typography>
              <Divider style={{ marginTop: "15px", marginBottom: "15px" }} />
            </Grid>
            <RelatedPersonCreate />
          </Step>

          <Step
            label={translate("tabs.header.address")}
            validationSchema={
              !existCustomer ? addressValidationSchema(addressLocations) : null
            }
          >
            <CustomerAddressCreate />
          </Step>
          <Step
            label={translate("tabs.header.service")}
            validationSchema={serviceValidationSchema}
          >
            <ServiceCreate />
          </Step>
          <Step
            label={translate("tabs.header.documents")}
            validationSchema={documentValidationSchema}
          >
            <DocumentCreate />
          </Step>
        </Stepper>
      </PageLayout>
    </>
  );
};

export default ApplicationCreate;
//Farkli bir bayi musterisi oldugu icin bilgileri gorme yetkiniz yoktur.
